import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import { CaretDownIcon } from '@phosphor-icons/react';
import { Container, DisplayHeading, Eyebrow } from '@/travo/components/ui/postmark';
import { useCopy } from '@/travo/i18n';
import { HARD_SHADOW, INK_RULE } from '@/travo/theme/constants';

interface FAQItemProps {
  question: string;
  answer: string;
}

/**
 * One question as a native <details>: the answer is in the HTML (prerendered, crawlable,
 * works without JS), and the visible text matches the FAQPage JSON-LD on the page.
 */
const FAQItem = ({ question, answer }: FAQItemProps) => (
  <Box as="details" borderBottom={INK_RULE.line} css={{ '&[open] .faq-caret': { transform: 'rotate(180deg)' } }}>
    <Box
      as="summary"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap="16px"
      py="18px"
      cursor="pointer"
      listStyleType="none"
      css={{ '&::-webkit-details-marker': { display: 'none' } }}
    >
      <Heading as="h3" fontFamily="body" fontSize={{ base: '17px', md: '19px' }} fontWeight="700" lineHeight="1.4">
        {question}
      </Heading>
      <Box className="faq-caret" flex="none" color="stamp" transition="transform 0.2s">
        <CaretDownIcon size={20} weight="bold" aria-hidden />
      </Box>
    </Box>
    <Text pb="20px" pr={{ base: 0, md: '40px' }} color="inkSoft">
      {answer}
    </Text>
  </Box>
);

/** Visible FAQ — the same questions the FAQPage structured data declares. */
const FAQ = () => {
  const { faq, faqSection } = useCopy();

  return (
    <Container as="section" id="faq" pb={{ base: '80px', md: '112px' }}>
      <Flex wrap="wrap" gap={{ base: '32px', md: '64px' }}>
        <Flex direction="column" gap="16px" flex="1 1 300px" minW={0}>
          <Eyebrow>{faqSection.eyebrow}</Eyebrow>
          <DisplayHeading fontSize={{ base: '34px', md: '48px' }}>{faqSection.title}</DisplayHeading>
          <Text color="inkSoft" maxW="22em">
            {faqSection.body}
          </Text>
        </Flex>
        <Box flex="2 1 520px" minW={0} bg="card" border="2px solid" borderColor="ink" boxShadow={HARD_SHADOW.card} px={{ base: '20px', md: '32px' }} py="8px">
          {faq.map((item) => (
            <FAQItem key={item.question} question={item.question} answer={item.answer} />
          ))}
        </Box>
      </Flex>
    </Container>
  );
};

export default FAQ;
